import * as React from 'react';
import cn from 'classnames';
import * as Buttons from '@library/Buttons';
import HourSelect from '../../hours/HourSelect';
import Link from './Link';
import * as schedule from './schedule';

type Props = {
  name: string;
  spans: string[];
  // False while the place has no hours at all, so an empty day reads as unknown rather
  // than as a closure nobody recorded
  known: boolean;
  onToggleDay: () => void;
  onToggle24: () => void;
  onAddSpan: () => void;
  onUpdateSpan: (index: number, span: string) => void;
  onRemoveSpan: (index: number) => void;
};

function DayRow(props: Props) {
  const open = props.spans.length > 0;
  const open24 = props.spans[0] === schedule.OPEN_24_HRS;

  return (
    <div
      className="editor__hours__day"
      data-cy="daily-hours"
      role="group"
      aria-label={props.name}
    >
      <button
        type="button"
        data-cy="open-today"
        aria-pressed={open}
        className={cn('editor__hours__day__pill', {
          'editor__hours__day__pill--open': open
        })}
        onClick={props.onToggleDay}
      >
        <span className="editor__hours__day__dot" aria-hidden="true" />
        {props.name}
      </button>

      <div className="editor__hours__day__body">
        {!open && (
          <div className="editor__hours__day__actions">
            <span className="editor__hours__day__note">
              {props.known ? 'Closed all day' : 'Not set'}
            </span>
            <Link cy="set-hours" modifier="open" onClick={props.onToggleDay}>
              {'Set hours'}
            </Link>
          </div>
        )}

        {open24 && (
          <span className="editor__hours__day__note">{'Open 24 hours'}</span>
        )}

        {open &&
          !open24 &&
          props.spans.map((span, index) => {
            const { open: from, close: to } = schedule.splitSpan(span);
            const spanName =
              props.spans.length > 1
                ? `${props.name} hours ${index + 1}`
                : props.name;

            return (
              <div className="editor__hours__day__span" key={index}>
                <HourSelect
                  label={`${spanName} opening time`}
                  value={from}
                  onChange={(v) =>
                    props.onUpdateSpan(index, schedule.joinSpan(v, to))}
                />
                <span className="editor__hours__day__dash">{'—'}</span>
                <HourSelect
                  label={`${spanName} closing time`}
                  value={to}
                  onChange={(v) =>
                    props.onUpdateSpan(index, schedule.joinSpan(from, v))}
                />
                <Buttons.DeleteButton
                  cy="remove-span"
                  ariaLabel={`Remove ${spanName}`}
                  onDelete={() => props.onRemoveSpan(index)}
                />
              </div>
            );
          })}

        {open && (
          <div className="editor__hours__day__actions">
            <Link cy="open-twenty-four" onClick={props.onToggle24}>
              {open24 ? 'Set specific hours' : 'Open 24 hours'}
            </Link>

            {!open24 && (
              <Link cy="add-span" onClick={props.onAddSpan}>
                {'+ Add hours'}
              </Link>
            )}

            <Link cy="close-day" modifier="close" onClick={props.onToggleDay}>
              {'Close this day'}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default DayRow;
